// Build verification (QA verdicts) client. The endpoint is mounted under both
// /admin/apps-setup and /admin/qa so developers and QA each reach it with the
// section they hold — pick whichever door this account has.
import { api } from './client';
import { useAuthStore } from '../store/auth';

export const BUILD_QA_STATUSES = ['unverified', 'passed', 'failed'] as const;
export type BuildQaStatus = (typeof BUILD_QA_STATUSES)[number];

export const BUILD_QA_LABELS: Record<BuildQaStatus, string> = {
  unverified: 'Не проверен',
  passed: 'Прошел проверку',
  failed: 'Не прошел проверку',
};

export const BUILD_QA_STYLES: Record<BuildQaStatus, string> = {
  unverified: 'bg-surface-muted text-content-subtle',
  passed: 'bg-green-500/15 text-green-400',
  failed: 'bg-red-500/15 text-red-400',
};

export function buildQaStatus(value: string | null | undefined): BuildQaStatus {
  return (BUILD_QA_STATUSES as readonly string[]).includes(value ?? '')
    ? (value as BuildQaStatus)
    : 'unverified';
}

export interface BuildQaRow {
  id: string; app_id: string; app_name: string | null; bundle_id: string | null;
  build_type: 'apk' | 'aab'; status: string; version_name: string; version_code: number;
  artifact_r2_key: string | null; gh_run_url: string | null; error: string | null;
  base_repo_name: string | null; base_repo_branch: string | null;
  qa_status: string; qa_note: string | null; qa_by: string | null; qa_at: number | null;
  qa_by_name: string | null; qa_by_email: string | null;
  created_at: number; updated_at: number;
}

export interface BuildQaSummary { unverified: number; passed: number; failed: number; total: number; }

function basePath(): string {
  const admin = useAuthStore.getState().admin;
  const perms = Array.isArray(admin?.permissions) ? admin!.permissions : [];
  // A super admin holds everything; developers get apps-setup, QA gets qa.
  if (admin?.role === 'super_admin' || perms.includes('apps-setup')) return '/admin/apps-setup/build-qa';
  return '/admin/qa/build-qa';
}

export const getBuildQa = (params: { app_id?: string; qa_status?: BuildQaStatus; limit?: number } = {}) => {
  const q = new URLSearchParams();
  if (params.app_id) q.set('app_id', params.app_id);
  if (params.qa_status) q.set('qa_status', params.qa_status);
  if (params.limit) q.set('limit', String(params.limit));
  const qs = q.toString();
  return api.get<{ builds: BuildQaRow[]; summary: BuildQaSummary }>(`${basePath()}${qs ? `?${qs}` : ''}`);
};

export const setBuildQa = (buildId: string, patch: { qa_status?: BuildQaStatus; qa_note?: string }) =>
  api.patch<BuildQaRow>(`${basePath()}/${encodeURIComponent(buildId)}`, patch);
